import { useState, type ReactNode } from "react";
import { Dropdown } from "../ui/dropdown/Dropdown";

export interface SearchableOption {
  id: number;
  label: string;
  searchText?: string;
}

interface SearchableHeaderDropdownProps {
  options: SearchableOption[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  allLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;
  icon: ReactNode;
  widthClass: string;
  disabled?: boolean;
}

const optionClasses = (selected: boolean) =>
  `w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
    selected
      ? "bg-gray-100 text-brand-600 dark:bg-gray-700 dark:text-brand-400"
      : "text-gray-700 dark:text-gray-300"
  }`;

export default function SearchableHeaderDropdown({
  options,
  selectedId,
  onSelect,
  allLabel,
  searchPlaceholder,
  emptyMessage,
  icon,
  widthClass,
  disabled = false,
}: SearchableHeaderDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleSelect = (id: number | null) => {
    onSelect(id);
    closeDropdown();
  };

  const query = searchQuery.toLowerCase();
  const filteredOptions = options.filter((option) =>
    (option.searchText ?? option.label).toLowerCase().includes(query),
  );
  const selectedOption = options.find((option) => option.id === selectedId);

  return (
    <div className="relative">
      <button
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between ${widthClass} gap-2 px-3 py-2 text-sm font-medium rounded-lg dropdown-toggle text-gray-700 bg-gray-100 dark:bg-gray-800 dark:text-gray-300 ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200 dark:hover:bg-gray-700"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className="truncate">{selectedOption ? selectedOption.label : allLabel}</span>
        </div>
        <svg
          className={`w-4 h-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className={`absolute left-0 mt-2 ${widthClass} flex flex-col rounded-xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark`}
      >
        <div className="sticky top-0 p-2 bg-white dark:bg-gray-dark border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400"
            />
          </div>
        </div>

        <div className="max-h-[340px] overflow-y-auto">
          {!searchQuery && (
            <button onClick={() => handleSelect(null)} className={optionClasses(selectedId === null)}>
              {allLabel}
            </button>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleSelect(option.id)}
                className={optionClasses(selectedId === option.id)}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
              {emptyMessage}
            </div>
          )}
        </div>
      </Dropdown>
    </div>
  );
}
